import type { PowerDataPoint, StrokeBoundary } from './types';

/**
 * Validates if a stroke is complete (has all phases)
 * A complete stroke should start at catch/recovery (low power), not mid-drive (elevated power)
 */
export function isStrokeComplete(
  correctedData: PowerDataPoint[],
  strokeStart: number,
  _strokeEnd: number
): boolean {
  if (!correctedData || correctedData.length === 0) return false;

  // Find data point closest to stroke start
  const startPoint = correctedData.find(
    (point) => Math.abs((point as PowerDataPoint).time - strokeStart) < 0.1
  ) as PowerDataPoint | undefined;

  if (!startPoint) return false;

  // Get all rower keys
  const allRowerKeys = Object.keys(startPoint).filter((key) => key !== 'time');

  // Calculate average power at stroke start across all rowers
  let totalPower = 0;
  let powerCount = 0;
  allRowerKeys.forEach((key) => {
    const power = startPoint[key] as number;
    if (typeof power === 'number' && isFinite(power) && power >= 0) {
      totalPower += power;
      powerCount++;
    }
  });

  const averageStartPower = powerCount > 0 ? totalPower / powerCount : 0;

  // A complete stroke should start at low power (catch/recovery phase)
  // If power is already elevated (>50W), it likely started mid-drive (incomplete)
  const POWER_THRESHOLD = 50; // watts
  const isComplete = averageStartPower < POWER_THRESHOLD;

  return isComplete;
}

/**
 * Calculates first complete stroke start time from stroke bounds or power data
 */
export function calculateFirstStrokeStartTime(
  correctedData: PowerDataPoint[],
  dataMode: 'periodic' | 'aperiodic',
  strokeBounds: StrokeBoundary[] | undefined,
  timeExtent: [number, number]
): number | null {
  // Only for periodic mode
  if (dataMode !== 'periodic' || !correctedData || correctedData.length === 0) {
    return null;
  }

  // Try to use stroke bounds if available
  if (strokeBounds && strokeBounds.length > 0) {
    // Find first complete stroke (has both start and end)
    const firstCompleteStroke = strokeBounds.find((sb) => sb.start != null && sb.end != null);
    if (firstCompleteStroke) {
      // Convert stroke boundary from milliseconds to seconds
      const strokeStartMs = firstCompleteStroke.start;

      // Find the minimum stroke boundary timestamp (session start)
      const allStrokeStarts = strokeBounds.filter((sb) => sb.start != null).map((sb) => sb.start!);
      const sessionStartMs = Math.min(...allStrokeStarts);

      // Calculate relative time: (strokeStart - sessionStart) / 1000
      const relativeStrokeStartSeconds = (strokeStartMs - sessionStartMs) / 1000;

      // Validate and return
      if (relativeStrokeStartSeconds >= 0 && relativeStrokeStartSeconds <= timeExtent[1]) {
        return relativeStrokeStartSeconds;
      }
    }
  }

  // Fallback: Detect first stroke cycle from power data itself
  // Look for the first significant power peak (stroke cycle)
  // A stroke cycle typically shows: low power -> peak -> low power
  const POWER_THRESHOLD = 50; // Minimum power to consider as a stroke (watts)
  const MIN_STROKE_DURATION = 1.0; // Minimum stroke duration in seconds
  const MAX_STROKE_DURATION = 5.0; // Maximum stroke duration in seconds

  let inStroke = false;
  let strokeStartTime: number | null = null;

  for (let i = 0; i < correctedData.length; i++) {
    const point = correctedData[i] as PowerDataPoint;
    const time = point.time;

    // Get maximum power across all rowers at this time point
    let maxPower = 0;
    Object.keys(point).forEach((key) => {
      if (key !== 'time') {
        const power = point[key] as number;
        if (typeof power === 'number' && isFinite(power) && power > maxPower) {
          maxPower = power;
        }
      }
    });

    if (!inStroke && maxPower >= POWER_THRESHOLD) {
      // Start of a stroke - power exceeded threshold
      inStroke = true;
      strokeStartTime = time;
    } else if (inStroke && maxPower < POWER_THRESHOLD / 2) {
      // End of stroke - power dropped below half threshold
      if (strokeStartTime != null) {
        const strokeDuration = time - strokeStartTime;
        // Check if this is a complete stroke (reasonable duration)
        if (strokeDuration >= MIN_STROKE_DURATION && strokeDuration <= MAX_STROKE_DURATION) {
          // Found first complete stroke!
          return strokeStartTime;
        }
      }
      // Reset for next stroke
      inStroke = false;
      strokeStartTime = null;
    }
  }

  // If we found a stroke start but no end, check if it's still reasonable
  if (strokeStartTime != null && strokeStartTime > 0 && strokeStartTime < timeExtent[1] / 2) {
    return strokeStartTime;
  }

  return null;
}

/**
 * Calculates all stroke cycles from the full dataset
 * This ensures stroke numbers are consistent across pan/zoom
 */
export function calculateAllStrokeCycles(
  correctedData: PowerDataPoint[],
  dataMode: 'periodic' | 'aperiodic',
  strokeBounds: StrokeBoundary[] | undefined
): Array<{ start: number; end: number; strokeNumber: number }> {
  if (dataMode !== 'periodic' || !correctedData || correctedData.length === 0) {
    return [];
  }

  let strokeCycles: Array<{ start: number; end: number; strokeNumber: number }> = [];

  // Try to use stroke bounds if available
  if (strokeBounds && strokeBounds.length > 0) {
    const allStrokeStarts = strokeBounds.filter((sb) => sb.start != null).map((sb) => sb.start!);

    if (allStrokeStarts.length > 0) {
      // Filter strokes with both start and end
      const strokesWithEnds = strokeBounds.filter((sb) => sb.start != null && sb.end != null);

      // Validate first stroke for completeness - skip if incomplete
      let firstCompleteIndex = 0;
      if (strokesWithEnds.length > 0) {
        const firstStroke = strokesWithEnds[0];
        const sessionStartMs = Math.min(...allStrokeStarts);
        const firstStrokeStart = (firstStroke.start! - sessionStartMs) / 1000;
        const firstStrokeEnd = (firstStroke.end! - sessionStartMs) / 1000;

        if (!isStrokeComplete(correctedData, firstStrokeStart, firstStrokeEnd)) {
          // First stroke is incomplete - skip it and start from second stroke
          firstCompleteIndex = 1;
        }
      }

      // Use the first complete stroke's start as session start for consistent timing
      const sessionStartMs =
        firstCompleteIndex < strokesWithEnds.length
          ? strokesWithEnds[firstCompleteIndex].start!
          : Math.min(...allStrokeStarts);

      // Only include strokes starting from the first complete one
      strokeCycles = strokesWithEnds.slice(firstCompleteIndex).map((sb, index) => ({
        start: (sb.start! - sessionStartMs) / 1000,
        end: (sb.end! - sessionStartMs) / 1000,
        strokeNumber: index + 1,
      }));
    }
  } else {
    // Fallback: Detect stroke cycles from power data itself
    // Use ALL rower keys for detection (not just visible ones)
    const allRowerKeys = Object.keys(correctedData[0]).filter((key) => key !== 'time');

    // Calculate baseline power
    const allPowers: number[] = [];
    correctedData.forEach((point) => {
      allRowerKeys.forEach((key) => {
        const power = (point as PowerDataPoint)[key] as number;
        if (typeof power === 'number' && isFinite(power)) {
          allPowers.push(power);
        }
      });
    });

    const sortedPowers = [...allPowers].sort((a, b) => a - b);
    const baselineIndex = Math.floor(sortedPowers.length * 0.2);
    const baselinePower = sortedPowers[baselineIndex] || 0;

    // Find all local peaks
    const peaks: Array<{ time: number; power: number; index: number }> = [];

    for (let i = 1; i < correctedData.length - 1; i++) {
      const point = correctedData[i] as PowerDataPoint;
      const prevPoint = correctedData[i - 1] as PowerDataPoint;
      const nextPoint = correctedData[i + 1] as PowerDataPoint;

      let maxPower = 0;
      allRowerKeys.forEach((key) => {
        const power = point[key] as number;
        if (typeof power === 'number' && isFinite(power) && power > maxPower) {
          maxPower = power;
        }
      });

      let prevMaxPower = 0;
      allRowerKeys.forEach((key) => {
        const power = prevPoint[key] as number;
        if (typeof power === 'number' && isFinite(power) && power > prevMaxPower) {
          prevMaxPower = power;
        }
      });

      let nextMaxPower = 0;
      allRowerKeys.forEach((key) => {
        const power = nextPoint[key] as number;
        if (typeof power === 'number' && isFinite(power) && power > nextMaxPower) {
          nextMaxPower = power;
        }
      });

      if (maxPower > prevMaxPower && maxPower > nextMaxPower && maxPower > baselinePower + 30) {
        peaks.push({
          time: point.time,
          power: maxPower,
          index: i,
        });
      }
    }

    // Find valleys between peaks
    for (let p = 0; p < peaks.length; p++) {
      const peak = peaks[p];
      const peakIndex = peak.index;

      let valleyStartTime = peak.time;
      for (let i = peakIndex - 1; i >= 0; i--) {
        const point = correctedData[i] as PowerDataPoint;
        let maxPower = 0;
        allRowerKeys.forEach((key) => {
          const power = point[key] as number;
          if (typeof power === 'number' && isFinite(power) && power > maxPower) {
            maxPower = power;
          }
        });

        if (i > 0 && i < correctedData.length - 1) {
          const prevPoint = correctedData[i - 1] as PowerDataPoint;
          const nextPoint = correctedData[i + 1] as PowerDataPoint;

          let prevMaxPower = 0;
          let nextMaxPower = 0;
          allRowerKeys.forEach((key) => {
            const prevPower = prevPoint[key] as number;
            const nextPower = nextPoint[key] as number;
            if (typeof prevPower === 'number' && isFinite(prevPower) && prevPower > prevMaxPower) {
              prevMaxPower = prevPower;
            }
            if (typeof nextPower === 'number' && isFinite(nextPower) && nextPower > nextMaxPower) {
              nextMaxPower = nextPower;
            }
          });

          if (
            (maxPower < prevMaxPower && maxPower < nextMaxPower) ||
            maxPower <= baselinePower + 10
          ) {
            valleyStartTime = point.time;
            break;
          }
        }

        if (peak.time - point.time > 2) {
          break;
        }
      }

      let valleyEndTime = peak.time;
      for (let i = peakIndex + 1; i < correctedData.length; i++) {
        const point = correctedData[i] as PowerDataPoint;
        let maxPower = 0;
        allRowerKeys.forEach((key) => {
          const power = point[key] as number;
          if (typeof power === 'number' && isFinite(power) && power > maxPower) {
            maxPower = power;
          }
        });

        if (i > 0 && i < correctedData.length - 1) {
          const prevPoint = correctedData[i - 1] as PowerDataPoint;
          const nextPoint = correctedData[i + 1] as PowerDataPoint;

          let prevMaxPower = 0;
          let nextMaxPower = 0;
          allRowerKeys.forEach((key) => {
            const prevPower = prevPoint[key] as number;
            const nextPower = nextPoint[key] as number;
            if (typeof prevPower === 'number' && isFinite(prevPower) && prevPower > prevMaxPower) {
              prevMaxPower = prevPower;
            }
            if (typeof nextPower === 'number' && isFinite(nextPower) && nextPower > nextMaxPower) {
              nextMaxPower = nextPower;
            }
          });

          if (
            (maxPower < prevMaxPower && maxPower < nextMaxPower) ||
            maxPower <= baselinePower + 10
          ) {
            valleyEndTime = point.time;
            break;
          }
        }

        if (point.time - peak.time > 2) {
          break;
        }
      }

      const strokeDuration = valleyEndTime - valleyStartTime;
      if (strokeDuration >= 0.5 && strokeDuration <= 3.0) {
        strokeCycles.push({
          start: valleyStartTime,
          end: valleyEndTime,
          strokeNumber: strokeCycles.length + 1,
        });
      }
    }
  }

  // Validate first stroke for completeness in fallback detection too
  if (strokeCycles.length > 0) {
    const firstStroke = strokeCycles[0];
    if (!isStrokeComplete(correctedData, firstStroke.start, firstStroke.end)) {
      // First stroke is incomplete - skip it and renumber remaining strokes
      strokeCycles = strokeCycles.slice(1).map((stroke, index) => ({
        ...stroke,
        strokeNumber: index + 1,
      }));
    }
  }

  return strokeCycles;
}

/**
 * Filters stroke cycles to only those visible in the current window
 */
export function filterVisibleStrokes(
  allStrokeCycles: Array<{ start: number; end: number; strokeNumber: number }>,
  windowStart: number,
  windowEnd: number
): Array<{ start: number; end: number; strokeNumber: number }> {
  return allStrokeCycles.filter((stroke) => {
    // Stroke is visible if it overlaps with [windowStart, windowEnd]
    return stroke.end >= windowStart && stroke.start <= windowEnd;
  });
}
